// medplum-automations.js — ONLINE-PRIMARY (main-2) client port of the whole
// Automations surface. server/opd/automations.mjs already read/wrote FHIR
// DIRECTLY, run-as-you (getMedplumForUser) with NO Lock 1 at all — so this is a
// near-mechanical move of those same FHIR calls + their PURE shaping into the
// browser's own MedplumClient (the shared getMedplum() session, RLS-scoped by the
// doctor's own token — see medplum-client.js). Every shaper below is copied
// VERBATIM from automations.mjs (it isolated them behind an __test export precisely
// because they have zero server dependency), so a job/activity item round-trips
// through the client unchanged and Automations.jsx renders the SAME DTO.
//
// The scheduled-job record is a FHIR `Basic` (code
// `https://healther.io/opd/basic-type|scheduled-job`, the whole job payload packed
// into ONE extension `.../scheduled-job` as a JSON string — the exact shape
// bots/scheduled-job-dispatcher.js reads, so a job created/edited here fires with
// no dispatcher change). Reads = `searchResources('Basic', …)`; the write verbs
// (create / edit / status flip / per-recipient pause) are plain
// `create/update Resource` on that same Basic — all ported here.
//
// The registry endpoint (GET /automations/registry) is NOT FHIR: it read a
// server-filesystem manifest (bots/medplum.config.json) joined with a hardcoded
// description map. There is no browser-readable file, so we INLINE the same static
// list as AUTOMATIONS_REGISTRY below — faithfully copied from automations.mjs's
// BOT_REGISTRY + medplum.config.json's bot order — no fetch, no Medplum call.
//
// Screen JSX is unchanged (the mockups ARE the design — AGENTS.md §7): Automations.jsx
// only swaps its `api('/automations/…')` read call-sites for these functions
// (Medplum-first, Express fallback on throw — the same guard bridge.js uses).
import { getMedplum, deriveCurrentUser, invalidateCached } from './medplum-client.js';

const BASIC_TYPE_SYSTEM = 'https://healther.io/opd/basic-type';
const JOB_EXT_URL = 'https://healther.io/fhir/StructureDefinition/scheduled-job';
const SCHEDULED_JOB_CODE = `${BASIC_TYPE_SYSTEM}|scheduled-job`;

// ── scheduled-job codec (read the single-extension JSON; mirrors the dispatcher) ──
function readJob(basic) {
  const e = (basic && basic.extension || []).find((x) => x && x.url === JOB_EXT_URL && x.valueString);
  if (!e) return null;
  try { return JSON.parse(e.valueString); } catch (_) { return null; }
}
function writeJobExt(basic, job) {
  const extension = (Array.isArray(basic.extension) ? basic.extension.slice() : [])
    .filter((x) => x && x.url !== JOB_EXT_URL);
  extension.push({ url: JOB_EXT_URL, valueString: JSON.stringify(job) });
  return { ...basic, extension };
}

// ── human-readable summaries (copied verbatim from automations.mjs) ──────────────
const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// Short IANA-zone label so "Asia/Kolkata" reads as "IST" (the only v1 zone; others
// pass through their last path segment rather than a misleading "IST").
function zoneLabel(tz) {
  if (!tz || tz === 'Asia/Kolkata') return 'IST';
  return String(tz).split('/').pop().replace(/_/g, ' ');
}
function clock12(hour, minute) {
  const h = Number(hour); const m = Number(minute || 0);
  if (!Number.isFinite(h)) return '';
  const ampm = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}
/** "Every Tue 7:00 PM IST" / "Every day 9:00 AM IST" / "Custom schedule". */
export function scheduleSummary(schedule) {
  if (!schedule || typeof schedule !== 'object') return 'Custom schedule';
  const t = clock12(schedule.hour, schedule.minute);
  const z = zoneLabel(schedule.timezone);
  if (schedule.freq === 'daily') return `Every day ${t} ${z}`.trim();
  if (schedule.freq === 'weekly') {
    const d = WEEKDAY[Number(schedule.dayOfWeek)] || '';
    return `Every ${d} ${t} ${z}`.replace(/\s+/g, ' ').trim();
  }
  return 'Custom schedule';
}
/**
 * "12 patients" / "A saved patient search" / "5 patients + 2 team members".
 *
 * A job's recipients are any mix of three things: hand-picked PATIENTS, hand-picked
 * STAFF (cohort.staff — the owner's 2026-08-05 ask: "are there automations from the
 * doctor's side or nurse's side?"), and a saved patient search.
 */
export function cohortSummary(cohort) {
  if (!cohort || typeof cohort !== 'object') return 'No recipients';
  const n = Array.isArray(cohort.patients) ? cohort.patients.length : 0;
  const st = Array.isArray(cohort.staff) ? cohort.staff.length : 0;
  const hasQuery = typeof cohort.query === 'string' && cohort.query.trim();
  const parts = [];
  if (n) parts.push(`${n} patient${n === 1 ? '' : 's'}`);
  if (st) parts.push(`${st} team member${st === 1 ? '' : 's'}`);
  if (hasQuery) parts.push(parts.length ? 'a saved search' : 'A saved patient search');
  if (!parts.length) return 'No recipients';
  return parts.join(' + ');
}
// ── the create-task action's own vocabulary ─────────────────────────────────────
// Mirrors the Tasks screen's TASK_TYPE_POOL / medplum-tasks.js TASK_TYPE_LABELS
// verbatim, so a task minted by an automation is indistinguishable from one a
// clinician creates by hand on the Tasks screen (same code system, same labels,
// same filter chips).
export const TASK_TYPES = [
  'review-report', 'follow-up-call', 'verify-id', 'sign-off',
  'collect-sample', 'chase-result', 'refill', 'general',
];
export const TASK_TYPE_LABELS = {
  'review-report': 'Review result / report',
  'follow-up-call': 'Call patient / follow-up',
  'verify-id': 'Verify patient ID',
  'sign-off': 'Senior sign-off needed',
  'collect-sample': 'Collect lab sample',
  'chase-result': 'Chase pending result',
  refill: 'Prescription refill',
  general: 'General task',
};
const TASK_TYPE_SET = new Set(TASK_TYPES);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** "“Call about BP” · Call patient / follow-up" — the task a job will mint. */
export function taskSummary(task) {
  if (!task || typeof task !== 'object') return 'A follow-up task';
  const title = String(task.title || '').trim();
  const label = TASK_TYPE_LABELS[task.taskType] || null;
  if (title && label) return `“${title}” · ${label}`;
  if (title) return `“${title}”`;
  return label || 'A follow-up task';
}

function taskPreview(task) {
  if (!task || typeof task !== 'object') return null;
  const title = String(task.title || '').trim();
  if (!title) return null;
  return {
    title,
    taskType: task.taskType || 'general',
    taskTypeLabel: TASK_TYPE_LABELS[task.taskType] || task.taskType || 'General task',
    ownerId: task.ownerId || null,
    ownerName: task.ownerName || null,
  };
}

function messagePreview(message) {
  const text = String(message && message.text || '').trim();
  if (!text) return null;
  const channel = message && message.channel ? String(message.channel) : null;
  return { text: text.slice(0, 200), channel };
}
const refId = (ref) => String(ref || '').split('/')[1] || null;
const refDisplay = (r) => (r && r.display) || ((r && r.reference) || '').replace(/^[^/]+\//, '') || null;

/** Shape one scheduled-job Basic into the card the screen renders. */
export function shapeJob(basic) {
  const job = readJob(basic) || {};
  return {
    id: basic.id,
    status: job.status || 'active',                 // active | paused | cancelled
    action: job.action || 'send-message',
    scheduleSummary: scheduleSummary(job.schedule),
    cohortSummary: cohortSummary(job.cohort),
    message: messagePreview(job.message),
    task: taskPreview(job.task),
    lastRun: job.lastRun || null,
    nextRun: job.nextRun || null,
    author: refDisplay(basic.author) || refDisplay(basic.subject) || null,
    updatedAt: (basic.meta && basic.meta.lastUpdated) || null,
  };
}

// ── plain-English explanation of a job (action + schedule + cohort + message) ───
// One readable sentence the screen shows verbatim — "Every Tue 7:00 PM IST, send a
// WhatsApp message to 12 patients." No raw JSON ever reaches the UI.
const ACTION_VERB = {
  'send-message': 'send a message',
  'create-task': 'create a follow-up task',
};
export function explainJob(job) {
  const when = scheduleSummary(job && job.schedule);
  const who = cohortSummary(job && job.cohort);
  // A task job explains itself with its OWN title + type, not the message verb —
  // "create a “Call about BP” task (Call patient / follow-up) for 3 patients."
  if (job && job.action === 'create-task') {
    const t = (job.task && typeof job.task === 'object') ? job.task : {};
    const title = String(t.title || '').trim();
    const typeLabel = TASK_TYPE_LABELS[t.taskType] || null;
    const named = title ? `a “${title}” task` : 'a follow-up task';
    const typed = typeLabel ? ` (${typeLabel})` : '';
    return `${when}, create ${named}${typed} for ${who}.`;
  }
  const verb = ACTION_VERB[job && job.action] || 'run';
  const channel = job && job.action === 'send-message' && job.message && job.message.channel
    ? ` over ${job.message.channel}` : '';
  return `${when}, ${verb}${channel} to ${who}.`;
}

// ── cohort → NAMED people (the "affected people" list the detail view renders) ──
// Mirrors the dispatcher's resolveCohort, but resolves each ref to a display name
// AND keeps each person's excluded flag (the job's `excluded` array = per-individual
// pauses). Capped so a huge saved-search cohort never blows up the payload.
const MEMBER_CAP = 200;
const refKey = (r) => (typeof r === 'string' ? r : (r && r.reference)) || null;

/** Best human name from a FHIR Patient.name (given+family, or text). */
function patientName(p) {
  const n = (p && p.name || [])[0];
  if (!n) return null;
  if (n.text) return n.text;
  const given = Array.isArray(n.given) ? n.given.join(' ') : (n.given || '');
  return [given, n.family].filter(Boolean).join(' ').trim() || null;
}

async function resolveMembers(medplum, job) {
  const cohort = job && job.cohort && typeof job.cohort === 'object' ? job.cohort : {};
  const excluded = new Set((Array.isArray(job && job.excluded) ? job.excluded : []).map(refKey).filter(Boolean));
  const order = [];                 // preserve discovery order
  const names = new Map();          // 'Patient/x' → display name (best-effort)
  const origin = new Map();         // 'Patient/x' → 'static' | 'query' | 'staff'
  const seen = new Set();
  // `src` tags each ref with where it came from so the UI can pick the honest
  // Remove verb per person: a ref on the static cohort.patients list can be
  // genuinely dropped; a ref that only a query matches can only be `excluded`
  // (see the two write verbs below). First writer wins — a ref that is BOTH
  // explicitly listed AND query-matched stays 'static' (it can be dropped).
  const add = (ref, src) => { if (ref && /^Patient\//.test(ref) && !seen.has(ref) && seen.size < MEMBER_CAP) { seen.add(ref); order.push(ref); origin.set(ref, src); } };

  // (1) static patient refs — name them by reading each Patient.
  for (const r of (Array.isArray(cohort.patients) ? cohort.patients : [])) add(refKey(r), 'static');

  // (2) saved FHIR search — same split/subject-pull logic as the dispatcher.
  const query = typeof cohort.query === 'string' ? cohort.query.trim() : '';
  if (query && seen.size < MEMBER_CAP) {
    let type = 'Patient', params = '';
    const m = query.match(/^([A-Za-z]+)\?(.*)$/);
    if (m) { type = m[1]; params = m[2]; } else { params = query.replace(/^\?/, ''); }
    let found = [];
    try { found = await medplum.searchResources(type, params) || []; } catch (_) { found = []; }
    for (const res of found) {
      if (res.resourceType === 'Patient') {
        add('Patient/' + res.id, 'query');
        if (res.name) names.set('Patient/' + res.id, patientName(res));
        continue;
      }
      add((res.subject && res.subject.reference) || (res.for && res.for.reference) || (res.patient && res.patient.reference), 'query');
    }
  }

  // (3) hand-picked STAFF recipients (cohort.staff) — the doctor's/nurse's own side
  //     of automations. Same list, tagged origin 'staff' so the card can label them
  //     and the remove verb knows which array to edit.
  for (const r of (Array.isArray(cohort.staff) ? cohort.staff : [])) {
    const ref = refKey(r);
    if (ref && /^Practitioner\//.test(ref) && !seen.has(ref) && seen.size < MEMBER_CAP) {
      seen.add(ref); order.push(ref); origin.set(ref, 'staff');
    }
  }

  // Name any still-unnamed ref by reading its resource (best-effort; ref-id fallback).
  for (const ref of order) {
    if (names.has(ref)) continue;
    const type = ref.split('/')[0];
    try {
      const p = await medplum.readResource(type, refId(ref));
      names.set(ref, patientName(p) || refId(ref));
    } catch (_) { names.set(ref, refId(ref)); }
  }

  return order.map((ref) => ({
    ref, id: refId(ref), name: names.get(ref) || refId(ref),
    kind: /^Practitioner\//.test(ref) ? 'staff' : 'patient',
    excluded: excluded.has(ref), origin: origin.get(ref) || 'query',
  }));
}

// ── occurrence math (ported verbatim from bots/schedule-job-operation.js via
//    automations.mjs so the nextRun we stamp at CREATE agrees with the dispatcher) ──
// v1 supports Asia/Kolkata (IST, UTC+5:30, no DST) only — the same constraint the
// operation + dispatcher enforce. Used solely to seed nextRun so a new card shows
// "Next in Xd" immediately; the dispatcher re-derives it on each fire.
const IST_OFFSET_MIN = 5 * 60 + 30;
function istParts(nowMs) {
  const shifted = new Date(nowMs + IST_OFFSET_MIN * 60000);
  return {
    weekday: shifted.getUTCDay(),
    minutesOfDay: shifted.getUTCHours() * 60 + shifted.getUTCMinutes(),
    istMidnightUtcMs: Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth(), shifted.getUTCDate()) - IST_OFFSET_MIN * 60000,
  };
}
function lastOccurrenceMs(schedule, nowMs) {
  if (!schedule || typeof schedule !== 'object') return null;
  const hour = Number(schedule.hour), minute = Number(schedule.minute || 0);
  if (!Number.isFinite(hour) || hour < 0 || hour > 23 || !Number.isFinite(minute) || minute < 0 || minute > 59) return null;
  const targetMinOfDay = hour * 60 + minute;
  const DAY = 24 * 60 * 60 * 1000;
  const { weekday, minutesOfDay, istMidnightUtcMs } = istParts(nowMs);
  const todayOccMs = istMidnightUtcMs + targetMinOfDay * 60000;
  if (schedule.freq === 'daily') return minutesOfDay >= targetMinOfDay ? todayOccMs : todayOccMs - DAY;
  if (schedule.freq === 'weekly') {
    const dow = Number(schedule.dayOfWeek);
    if (!Number.isFinite(dow) || dow < 0 || dow > 6) return null;
    let backDays = (weekday - dow + 7) % 7;
    if (backDays === 0 && minutesOfDay < targetMinOfDay) backDays = 7;
    return todayOccMs - backDays * DAY;
  }
  return null;
}
function nextOccurrenceMs(schedule, nowMs) {
  const DAY = 24 * 60 * 60 * 1000;
  const last = lastOccurrenceMs(schedule, nowMs);
  if (last == null) return null;
  const step = schedule.freq === 'daily' ? DAY : 7 * DAY;
  let next = last;
  while (next <= nowMs) next += step;
  return next;
}

// ── validation for the editable fields (schedule / cohort / message) ────────────
const MSG_CHANNELS = new Set(['whatsapp', 'sms', 'inapp']);
function badReq(msg) { const e = new Error(msg); e.status = 400; return e; }
function validateSchedule(s) {
  if (s == null) return;
  if (typeof s !== 'object') throw badReq('schedule must be an object');
  if (!['daily', 'weekly'].includes(s.freq)) throw badReq('schedule.freq must be daily or weekly');
  const hour = Number(s.hour), minute = Number(s.minute);
  if (!Number.isFinite(hour) || hour < 0 || hour > 23) throw badReq('schedule.hour must be 0–23');
  if (!Number.isFinite(minute) || minute < 0 || minute > 59) throw badReq('schedule.minute must be 0–59');
  if (s.freq === 'weekly') {
    const dow = Number(s.dayOfWeek);
    if (!Number.isFinite(dow) || dow < 0 || dow > 6) throw badReq('weekly schedule needs dayOfWeek 0–6');
  }
}
function validateCohort(c) {
  if (c == null) return;
  if (typeof c !== 'object' || Array.isArray(c)) throw badReq('cohort must be an object');
  if (c.patients != null && !Array.isArray(c.patients)) throw badReq('cohort.patients must be an array');
  if (c.staff != null && !Array.isArray(c.staff)) throw badReq('cohort.staff must be an array');
  if (c.query != null && typeof c.query !== 'string') throw badReq('cohort.query must be a string');
}
function validateMessage(m) {
  if (m == null) return;
  if (typeof m !== 'object') throw badReq('message must be an object');
  if (m.text != null && typeof m.text !== 'string') throw badReq('message.text must be a string');
  if (m.channel != null && !MSG_CHANNELS.has(m.channel)) throw badReq('message.channel must be whatsapp, sms or inapp');
}
function validateTask(t) {
  if (t == null) return;
  if (typeof t !== 'object' || Array.isArray(t)) throw badReq('task must be an object');
  if (t.title != null && typeof t.title !== 'string') throw badReq('task.title must be a string');
  if (t.taskType != null && !TASK_TYPE_SET.has(t.taskType)) throw badReq(`task.taskType must be one of ${TASK_TYPES.join(', ')}`);
  // The assignee is a Medplum Practitioner UUID (the same id the Tasks screen's
  // Assignee select hands createTaskInMedplum). A slug/name here would mint a
  // dangling Task.owner reference every single time the job fires — reject loud.
  if (t.ownerId != null && t.ownerId !== '' && !UUID_RE.test(String(t.ownerId))) {
    throw badReq('task.ownerId must be a Medplum Practitioner id');
  }
}

// ── jobs: list + detail (run-as-you via the shared client) ──────────────────────
async function fetchJobs(medplum) {
  const basics = await medplum.searchResources('Basic',
    `code=${encodeURIComponent(SCHEDULED_JOB_CODE)}&_sort=-_lastUpdated&_count=200`);
  const rows = (basics || []).map(shapeJob);
  // Active first, then paused, then cancelled; newest within each band.
  const rank = { active: 0, paused: 1, cancelled: 2 };
  rows.sort((a, b) => (rank[a.status] ?? 3) - (rank[b.status] ?? 3)
    || String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')));
  return rows;
}

// ── bucket a shaped job into the three status groups the screen renders ──────────
// The owner's ask (2026-07-07): group scheduled jobs by status so CANCELLED cards
// leave the prime position — Upcoming (still runs) first, then Done, then Cancelled.
//
// The scheduled-job data model has NO one-shot / expiry concept: every job is a
// recurring daily/weekly schedule whose status is only active | paused | cancelled,
// and a job that fires just gets its lastRun/nextRun re-stamped (it stays 'active').
// So a job is bucketed HONESTLY, not fabricated:
//   • cancelled — the explicit terminal status (status === 'cancelled').
//   • done      — a job that has genuinely finished: an explicit terminal status
//                 (done/completed/expired, defensive/future-proof) OR a non-cancelled
//                 job that has already run (lastRun set) yet has NO future occurrence
//                 (nextRun missing) — i.e. it can never run again. Normal recurring
//                 jobs always carry a nextRun, so they never fall here.
//   • upcoming  — everything still set up to run (active or paused).
// Pure + total (returns all three keys always) so it's unit-testable offline and
// through a synthetic DTO. Order within a bucket is preserved from the input (which
// fetchJobs already sorted active→paused→cancelled, newest-first within each band).
const DONE_STATUSES = new Set(['done', 'completed', 'expired', 'finished']);
export function jobBucket(job) {
  const status = job && job.status;
  if (status === 'cancelled') return 'cancelled';
  if (DONE_STATUSES.has(status)) return 'done';
  // A non-cancelled job that already ran but has no future run left = finished.
  if (job && job.lastRun && !job.nextRun) return 'done';
  return 'upcoming';
}
export function groupJobs(jobs) {
  const groups = { upcoming: [], done: [], cancelled: [] };
  for (const j of (Array.isArray(jobs) ? jobs : [])) groups[jobBucket(j)].push(j);
  return groups;
}

/** Load the scheduled-job cards. Returns { jobs } — the { jobs } the screen feeds. */
export async function loadJobsFromMedplum() {
  const { medplum } = await getMedplum();
  return { jobs: await fetchJobs(medplum) };
}

/**
 * Full detail for ONE job: the shaped card + a plain-English explanation, the
 * raw editable fields (schedule/cohort/message so the edit modal round-trips),
 * and the named people it affects. Same shape as GET /automations/jobs/:id →
 * { job }, so JobCard.loadDetail is unchanged.
 */
export async function loadJobDetailFromMedplum(id) {
  const { medplum } = await getMedplum();
  const basic = await medplum.readResource('Basic', id);
  if (!basic) { const e = new Error('job-not-found'); e.status = 404; throw e; }
  const job = readJob(basic);
  if (!job) { const e = new Error('not a scheduled-job'); e.status = 422; throw e; }
  const members = await resolveMembers(medplum, job);
  return {
    job: {
      ...shapeJob(basic),
      explanation: explainJob(job),
      members,
      schedule: job.schedule || null,
      cohort: job.cohort || null,
      rawMessage: job.message || null,
      rawTask: job.task || null,
      excluded: Array.isArray(job.excluded) ? job.excluded : [],
    },
  };
}

// ── activity feed: recent automation OUTPUT (Tasks + Communications + job fires) ──
// Automation output has no single tag, so we identify it by SHAPE per source
// (copied verbatim from automations.mjs).
const BOT_TASK_TYPES = {
  'review-report': { label: 'Flagged an abnormal result for review', bot: 'review-report-task' },
  'verify-id': { label: 'Flagged a possible duplicate patient', bot: 'patient-dedup-task' },
  'respond-to-message': { label: 'Flagged an unanswered patient message', bot: 'respond-to-message-task' },
};
function taskTypeCode(task) {
  const coding = (task && task.code && task.code.coding || []).find((c) => c && c.system === 'https://healther.io/opd/task-type');
  return (coding && coding.code) || (task && task.code && task.code.text) || 'general';
}
// A task minted by a SCHEDULED JOB carries the dispatcher's own idempotency
// identifier — unambiguous automation output whatever its task type, so it is
// recognised by that rather than by a fixed type list.
const SCHEDULED_TASK_SYSTEM = 'https://healther.io/opd/scheduled-task';
function scheduledJobTask(task) {
  return (task && task.identifier || []).some((i) => i && i.system === SCHEDULED_TASK_SYSTEM);
}
function looksBotTask(task) {
  if (scheduledJobTask(task)) return true;
  // A human requester (requester.display set by the app) means staff-created — skip.
  if (task && task.requester && task.requester.display) return false;
  return Object.prototype.hasOwnProperty.call(BOT_TASK_TYPES, taskTypeCode(task));
}
const BOT_COMM_CATEGORIES = {
  'appointment-reminder': { label: 'Sent an appointment reminder', bot: 'appointment-reminder' },
  'scheduled-message': { label: 'Sent a scheduled message', bot: 'scheduled-job-dispatcher' },
  'overdue-task-escalation': { label: 'Escalated an overdue task', bot: 'overdue-task-sweeper' },
};
function commCategoryCode(comm) {
  for (const cat of (comm && comm.category || [])) {
    const code = (cat && cat.coding && cat.coding[0] && cat.coding[0].code) || (cat && cat.text);
    if (code && Object.prototype.hasOwnProperty.call(BOT_COMM_CATEGORIES, code)) return code;
  }
  return null;
}

async function fetchActivity(medplum, limit) {
  const cap = Math.max(1, Math.min(200, Number(limit) || 50));
  const items = [];

  // 1. Bot-born Tasks (newest first; filter to the bot task SHAPES).
  try {
    const tasks = await medplum.searchResources('Task', `_sort=-_lastUpdated&_count=${cap * 2}`);
    for (const t of (tasks || [])) {
      if (!looksBotTask(t)) continue;
      const meta = BOT_TASK_TYPES[taskTypeCode(t)]
        || { label: `Created a ${TASK_TYPE_LABELS[taskTypeCode(t)] || 'task'}`, bot: 'scheduled-job-dispatcher' };
      items.push({
        kind: 'task',
        what: t.description || meta.label,
        detail: meta.label,
        sourceBot: meta.bot,
        patient: refDisplay(t.for) || null,
        status: t.status || null,
        when: t.authoredOn || (t.meta && t.meta.lastUpdated) || null,
        ref: `Task/${t.id}`,
      });
    }
  } catch (_) { /* online-only; a read miss just thins the feed */ }

  // 2. Bot Communications (reminders, scheduled sends, escalations).
  try {
    const comms = await medplum.searchResources('Communication', `_sort=-_lastUpdated&_count=${cap * 2}`);
    for (const c of (comms || [])) {
      const code = commCategoryCode(c);
      if (!code) continue;
      const meta = BOT_COMM_CATEGORIES[code];
      const contentString = c.payload && c.payload[0] && c.payload[0].contentString;
      items.push({
        kind: 'communication',
        what: contentString
          ? `${meta.label}${refDisplay(c.subject) ? ` to ${refDisplay(c.subject)}` : ''}`
          : meta.label,
        detail: (contentString && contentString.slice(0, 160)) || null,
        sourceBot: meta.bot,
        patient: refDisplay(c.subject) || refDisplay(c.recipient && c.recipient[0]) || null,
        status: c.status || null,
        when: c.sent || (c.meta && c.meta.lastUpdated) || null,
        ref: `Communication/${c.id}`,
      });
    }
  } catch (_) { /* online-only */ }

  // 3. Scheduled-job last-fires (the dispatcher stamps lastRun on each Basic it fires).
  try {
    const basics = await medplum.searchResources('Basic',
      `code=${encodeURIComponent(SCHEDULED_JOB_CODE)}&_count=200`);
    for (const b of (basics || [])) {
      const job = readJob(b);
      if (!job || !job.lastRun) continue;
      items.push({
        kind: 'job-fire',
        what: `Ran a scheduled job — ${cohortSummary(job.cohort)}`,
        detail: scheduleSummary(job.schedule),
        sourceBot: 'scheduled-job-dispatcher',
        patient: null,
        status: job.status || null,
        when: job.lastRun,
        ref: `Basic/${b.id}`,
      });
    }
  } catch (_) { /* online-only */ }

  items.sort((a, b) => String(b.when || '').localeCompare(String(a.when || '')));
  return items.slice(0, cap);
}

/** Load the recent-activity feed. Returns { activity } — the { activity } the screen feeds. */
export async function loadActivityFromMedplum(limit = 50) {
  const { medplum } = await getMedplum();
  return { activity: await fetchActivity(medplum, limit) };
}

/**
 * The whole-surface loader the orchestrator registers under the 'automations'
 * route in bridge.js (the DTO both live fetches feed, plus the static registry).
 * jobs + activity are read independently so the screen keeps its independent
 * jobsErr/activityErr states when it calls loadJobsFromMedplum/loadActivityFromMedplum
 * directly; this convenience loader is for the bootstrap contract.
 */
export async function loadAutomationsFromMedplum() {
  const { medplum, hospital } = await getMedplum();
  const [jobs, activity] = await Promise.all([fetchJobs(medplum), fetchActivity(medplum, 50)]);
  return {
    source: 'medplum-client-shaping',
    jobs,
    activity,
    automations: AUTOMATIONS_REGISTRY,
    currentUser: await deriveCurrentUser(medplum),
    hospital,
  };
}

// ── write verbs: all plain FHIR create/update on the scheduled-job Basic ─────────
// Ported from automations.mjs verbatim (createJob / editJob / setJobStatus /
// setRecipientPaused): read the Basic, mutate the job JSON in its extension, write
// it back run-as-you. Each returns the SAME { job } / { job, members } the Express
// routes did, so Automations.jsx's onCreate/onEdit/onAction/onToggleMember are
// unchanged. Fail loud — the caller (screen) catches and Express-fallbacks.

/** The dispatcher's exact `job.task` shape — only the fields the Bot reads. */
function normTask(task) {
  const out = {
    title: String(task.title).trim(),
    taskType: task.taskType,
  };
  if (task.ownerId) out.ownerId = String(task.ownerId);
  // A display name is stored alongside so a job card can name the assignee without
  // a Practitioner read; it is NOT authoritative (the Bot writes owner from ownerId).
  if (task.ownerName) out.ownerName = String(task.ownerName).trim();
  return out;
}

/**
 * DEPLOY-WINDOW COMPATIBILITY (delete once the updated dispatcher is live).
 *
 * The dispatcher Bot runs its DEPLOYED source, not this repo's. The deployed
 * create-task branch reads the task's wording from `job.message.text` and knows
 * nothing about `job.task`; without this mirror, every task it mints during the
 * window between shipping this app build and the next owner-gated Bot deployment
 * would be described "Scheduled follow-up" instead of the title the doctor typed.
 * So the title is mirrored into `message.text` — deliberately with NO channel, so
 * nothing can mistake a task job for a message job (validateMessage allows it, the
 * card renders the task block, and the updated Bot prefers `job.task`).
 */
function taskCompatMessage(task) {
  return { message: { text: String(task.title).trim() } };
}

// The two things a scheduled job can DO. Both are executed by the ONE dispatcher
// Bot (bots/scheduled-job-dispatcher.js) — the action is data on the job record,
// not a second Bot.
export const JOB_ACTIONS = ['send-message', 'create-task'];

/** Create a new scheduled-job Basic (POST /automations/jobs). Returns { job }. */
export async function createJobViaMedplum(body) {
  const { medplum } = await getMedplum();
  const schedule = body && body.schedule;
  const cohort = body && body.cohort;
  const message = body && body.message;
  const task = body && body.task;
  // Default stays 'send-message' so every existing caller (and every job already
  // in the database) behaves EXACTLY as before.
  const action = (body && body.action) || 'send-message';
  if (!JOB_ACTIONS.includes(action)) throw badReq(`action must be ${JOB_ACTIONS.join(' or ')}`);

  // PRESENCE is required at create (field-validators treat null as "leave
  // unchanged", right for edit but wrong for create). Assert loudly, then reuse
  // the same shape-validators the edit path uses.
  if (schedule == null) throw badReq('schedule is required');
  validateSchedule(schedule);
  if (cohort == null) throw badReq('cohort is required (at least one patient or a saved search)');
  validateCohort(cohort);
  const hasPatients = Array.isArray(cohort.patients) && cohort.patients.length > 0;
  const hasStaff = Array.isArray(cohort.staff) && cohort.staff.length > 0;
  const hasQuery = typeof cohort.query === 'string' && cohort.query.trim().length > 0;
  if (!hasPatients && !hasStaff && !hasQuery) throw badReq('cohort must have at least one patient, one team member, or a saved search');
  if (hasPatients) {
    for (const r of cohort.patients) {
      const ref = typeof r === 'string' ? r : (r && r.reference);
      if (!/^Patient\/[^/]+$/.test(String(ref || ''))) throw badReq('cohort.patients entries must be "Patient/<id>" references');
    }
  }
  if (hasStaff) {
    for (const r of cohort.staff) {
      const ref = typeof r === 'string' ? r : (r && r.reference);
      if (!/^Practitioner\/[^/]+$/.test(String(ref || ''))) throw badReq('cohort.staff entries must be "Practitioner/<id>" references');
    }
  }
  if (action === 'create-task') {
    if (task == null) throw badReq('task is required (a title and a task type)');
    validateTask(task);
    if (!task.title || !String(task.title).trim()) throw badReq('task.title is required');
    if (!TASK_TYPE_SET.has(task.taskType)) throw badReq(`task.taskType must be one of ${TASK_TYPES.join(', ')}`);
  } else {
    if (message == null) throw badReq('message is required (text + channel)');
    validateMessage(message);
    if (!message.text || !String(message.text).trim()) throw badReq('message.text is required');
    if (!MSG_CHANNELS.has(message.channel)) throw badReq('message.channel must be whatsapp, sms or inapp');
  }

  // Normalise to the dispatcher's exact job shape.
  const tz = schedule.timezone || 'Asia/Kolkata';
  const normSchedule = { freq: schedule.freq, hour: Number(schedule.hour), minute: Number(schedule.minute), timezone: tz };
  if (schedule.freq === 'weekly') normSchedule.dayOfWeek = Number(schedule.dayOfWeek);
  const normCohort = {};
  if (hasPatients) normCohort.patients = cohort.patients.map((r) => (typeof r === 'string' ? r : r.reference));
  if (hasStaff) normCohort.staff = cohort.staff.map((r) => (typeof r === 'string' ? r : r.reference));
  if (hasQuery) normCohort.query = cohort.query.trim();
  const nextMs = nextOccurrenceMs(normSchedule, Date.now());

  const job = {
    status: 'active',
    action,
    schedule: normSchedule,
    cohort: normCohort,
    ...(action === 'create-task'
      ? { task: normTask(task), ...taskCompatMessage(task) }
      : { message: { text: String(message.text), channel: message.channel } }),
    nextRun: nextMs != null ? new Date(nextMs).toISOString() : undefined,
  };

  // Stamp the author (run-as-you) so "my reminders" + attribution work — best-effort.
  let authorRef = null;
  try {
    const profile = typeof medplum.getProfile === 'function' ? medplum.getProfile() : null;
    if (profile && profile.resourceType && profile.id) authorRef = `${profile.resourceType}/${profile.id}`;
  } catch (_) { /* author is optional */ }
  if (authorRef) job.author = authorRef;

  const basic = writeJobExt({
    resourceType: 'Basic',
    code: { text: 'scheduled-job', coding: [{ system: BASIC_TYPE_SYSTEM, code: 'scheduled-job' }] },
    ...(authorRef ? { author: { reference: authorRef }, subject: { reference: authorRef } } : {}),
  }, job);

  const created = await medplum.createResource(basic);
  // Automations.jsx re-reads the scheduled-job Basic search immediately after
  // every mutation, and NOTHING else ever busts it: no screen opens a live wire
  // for Basic, so without this the list can show the pre-write job for the whole
  // client-cache window.
  await invalidateCached('Basic');
  return { job: shapeJob(created) };
}

/** Edit schedule / cohort / message (PATCH /automations/jobs/:id). Returns { job }. */
export async function editJobViaMedplum(id, patch) {
  const { medplum } = await getMedplum();
  const basic = await medplum.readResource('Basic', id);
  if (!basic) { const e = new Error('job-not-found'); e.status = 404; throw e; }
  const job = readJob(basic);
  if (!job) { const e = new Error('not a scheduled-job'); e.status = 422; throw e; }
  if (job.status === 'cancelled') { const e = new Error('job is cancelled and cannot be edited'); e.status = 409; throw e; }

  const next = { ...job };
  if (Object.prototype.hasOwnProperty.call(patch, 'schedule')) { validateSchedule(patch.schedule); next.schedule = patch.schedule; }
  if (Object.prototype.hasOwnProperty.call(patch, 'cohort'))   { validateCohort(patch.cohort);     next.cohort = patch.cohort; }
  if (Object.prototype.hasOwnProperty.call(patch, 'message'))  { validateMessage(patch.message);   next.message = { ...(job.message || {}), ...(patch.message || {}) }; }
  if (Object.prototype.hasOwnProperty.call(patch, 'task')) {
    validateTask(patch.task);
    const merged = { ...(job.task || {}), ...(patch.task || {}) };
    if (!merged.title || !String(merged.title).trim()) throw badReq('task.title is required');
    if (!TASK_TYPE_SET.has(merged.taskType)) throw badReq(`task.taskType must be one of ${TASK_TYPES.join(', ')}`);
    // An empty assignee means "unassign" — drop the key rather than storing ''.
    if (!merged.ownerId) { delete merged.ownerId; delete merged.ownerName; }
    next.task = normTask(merged);
    // Keep the deploy-window mirror in step with the title (see taskCompatMessage).
    if ((next.action || 'send-message') === 'create-task') next.message = taskCompatMessage(next.task).message;
  }
  // action is deliberately NOT editable — turning a message job into a task job
  // (or back) changes what it DOES; that's a new automation, not an edit.
  // status is deliberately NOT editable here — that's pause/resume/cancel.

  const saved = await medplum.updateResource(writeJobExt(basic, next));
  await invalidateCached('Basic');
  return { job: shapeJob(saved) };
}

const STATUS_BY_ACTION = { cancel: 'cancelled', pause: 'paused', resume: 'active' };
/**
 * Flip a job's status (POST /automations/jobs/:id/{cancel|pause|resume}). Takes
 * the ACTION verb (as the route did) so the screen's onAction is unchanged.
 * Returns { job }.
 */
export async function setJobStatusViaMedplum(id, action) {
  const status = STATUS_BY_ACTION[action];
  if (!status) { const e = new Error('unknown-action'); e.status = 404; throw e; }
  const { medplum } = await getMedplum();
  const basic = await medplum.readResource('Basic', id);
  if (!basic) { const e = new Error('job-not-found'); e.status = 404; throw e; }
  const job = readJob(basic);
  if (!job) { const e = new Error('not a scheduled-job'); e.status = 422; throw e; }
  // A cancelled job is terminal — don't resurrect it via pause/resume.
  if (job.status === 'cancelled' && status !== 'cancelled') {
    const e = new Error('job is cancelled and cannot be re-activated'); e.status = 409; throw e;
  }
  const saved = await medplum.updateResource(writeJobExt(basic, { ...job, status }));
  await invalidateCached('Basic');
  return { job: shapeJob(saved) };
}

/**
 * Per-individual pause: add/remove a patient ref from the job's `excluded`
 * (POST /automations/jobs/:id/recipient). Returns { job, members } so the card
 * updates its member list in place.
 */
export async function setRecipientPausedViaMedplum(id, patientRefOrId, paused) {
  const { medplum } = await getMedplum();
  // A recipient is a Patient OR (since 2026-08-05) a Practitioner on cohort.staff;
  // a bare id still means a patient, the historical caller shape.
  const raw = String(patientRefOrId || '').trim();
  const ref = /^(Patient|Practitioner)\//.test(raw) ? raw : ('Patient/' + raw);
  if (!/^(Patient|Practitioner)\/.+/.test(ref)) throw badReq('a patient or practitioner reference is required');
  const basic = await medplum.readResource('Basic', id);
  if (!basic) { const e = new Error('job-not-found'); e.status = 404; throw e; }
  const job = readJob(basic);
  if (!job) { const e = new Error('not a scheduled-job'); e.status = 422; throw e; }
  if (job.status === 'cancelled') { const e = new Error('job is cancelled'); e.status = 409; throw e; }

  const current = new Set((Array.isArray(job.excluded) ? job.excluded : []).map(refKey).filter(Boolean));
  if (paused) current.add(ref); else current.delete(ref);
  const next = { ...job, excluded: Array.from(current) };
  const saved = await medplum.updateResource(writeJobExt(basic, next));
  await invalidateCached('Basic');
  const members = await resolveMembers(medplum, readJob(saved) || next);
  return { job: shapeJob(saved), members };
}

// Shared normaliser + guarded read for the two cohort.patients mutators below.
function toPatientRef(patientRefOrId) {
  const raw = String(patientRefOrId || '').trim();
  const ref = /^(Patient|Practitioner)\//.test(raw) ? raw : ('Patient/' + raw);
  if (!/^(Patient|Practitioner)\/[^/]+$/.test(ref)) throw badReq('a patient or practitioner reference is required');
  return ref;
}
async function readEditableJob(medplum, id) {
  const basic = await medplum.readResource('Basic', id);
  if (!basic) { const e = new Error('job-not-found'); e.status = 404; throw e; }
  const job = readJob(basic);
  if (!job) { const e = new Error('not a scheduled-job'); e.status = 422; throw e; }
  if (job.status === 'cancelled') { const e = new Error('job is cancelled and cannot be edited'); e.status = 409; throw e; }
  return { basic, job };
}

/**
 * ADD a person to a job's STATIC recipient list (`cohort.patients`). Works whether
 * the job is static-only or also carries a query — the shape supports both at once
 * (cohortSummary renders "N patients + a saved search"), so an explicit add is the
 * honest way to include someone the query wouldn't match. Dedups; leaves any
 * existing `cohort.query` untouched. Returns { job, members } so the card refreshes
 * its member list in place — same contract as setRecipientPausedViaMedplum.
 */
export async function addRecipientViaMedplum(id, patientRefOrId) {
  const { medplum } = await getMedplum();
  const ref = toPatientRef(patientRefOrId);
  const { basic, job } = await readEditableJob(medplum, id);
  const cohort = job.cohort && typeof job.cohort === 'object' ? job.cohort : {};
  // Staff go on cohort.staff, patients on cohort.patients — same verb, right list.
  const key = /^Practitioner\//.test(ref) ? 'staff' : 'patients';
  const existing = (Array.isArray(cohort[key]) ? cohort[key] : []).map(refKey).filter(Boolean);
  if (existing.includes(ref)) { const members = await resolveMembers(medplum, job); return { job: shapeJob(basic), members }; }
  const nextCohort = { ...cohort, [key]: [...existing, ref] };
  const next = { ...job, cohort: nextCohort };
  const saved = await medplum.updateResource(writeJobExt(basic, next));
  await invalidateCached('Basic');
  const members = await resolveMembers(medplum, readJob(saved) || next);
  return { job: shapeJob(saved), members };
}

/**
 * REMOVE a person from a job's STATIC recipient list (`cohort.patients`) —
 * genuinely drops their ref (the honest semantics for a hand-picked list, vs the
 * togglable `excluded` pause). Only meaningful for members whose origin is
 * 'static'; a query-matched member cannot be removed this way (the UI routes
 * those to setRecipientPausedViaMedplum as an "excluded" removal instead — the
 * only honest option, since editing the query would silently change who else is
 * affected). Returns { job, members }.
 */
export async function removeRecipientFromCohortViaMedplum(id, patientRefOrId) {
  const { medplum } = await getMedplum();
  const ref = toPatientRef(patientRefOrId);
  const { basic, job } = await readEditableJob(medplum, id);
  const cohort = job.cohort && typeof job.cohort === 'object' ? job.cohort : {};
  const key = /^Practitioner\//.test(ref) ? 'staff' : 'patients';
  const other = key === 'staff' ? 'patients' : 'staff';
  const remaining = (Array.isArray(cohort[key]) ? cohort[key] : []).map(refKey).filter(Boolean).filter((r) => r !== ref);
  // Keep the cohort valid: a job must still have at least one recipient of SOME
  // kind (a patient, a team member, or a saved search).
  const hasQuery = typeof cohort.query === 'string' && cohort.query.trim().length > 0;
  const hasOther = Array.isArray(cohort[other]) && cohort[other].length > 0;
  if (!remaining.length && !hasQuery && !hasOther) { const e = new Error('a job must keep at least one recipient — pause it instead of removing its last person'); e.status = 409; throw e; }
  const nextCohort = { ...cohort };
  if (remaining.length) nextCohort[key] = remaining; else delete nextCohort[key];
  const next = { ...job, cohort: nextCohort };
  const saved = await medplum.updateResource(writeJobExt(basic, next));
  await invalidateCached('Basic');
  const members = await resolveMembers(medplum, readJob(saved) || next);
  return { job: shapeJob(saved), members };
}

/**
 * Name a list of "Patient/<id>" / "Practitioner/<id>" references so a picker can
 * show real chips for refs it did not pick itself (the natural-language prefill
 * hands back bare references). Best-effort per ref — an unreadable one falls back
 * to its id rather than dropping the recipient, which would silently change WHO
 * the automation is for.
 */
export async function resolveRecipientNames(refs) {
  const { medplum } = await getMedplum();
  const out = [];
  for (const r of (Array.isArray(refs) ? refs : [])) {
    const ref = refKey(r);
    if (!ref || !/^(Patient|Practitioner)\/[^/]+$/.test(ref)) continue;
    const [type, id] = ref.split('/');
    let name = id;
    try {
      const res = await medplum.readResource(type, id);
      name = patientName(res) || id;
    } catch (_) { /* keep the id — never drop a recipient */ }
    out.push({ ref, id, name, kind: type === 'Practitioner' ? 'staff' : 'patient' });
  }
  return out;
}

// ── registry: STATIC (inlined; not FHIR) ─────────────────────────────────────────
// The server read bots/medplum.config.json (a filesystem manifest) joined with a
// hardcoded BOT_REGISTRY description map. There is no browser-readable file, so the
// same static list is inlined here, faithfully copied from automations.mjs's
// BOT_REGISTRY (purpose/trigger) + the bot ORDER + label-casing (loadRegistry's
// name→label transform) from medplum.config.json. Ships in the SPA bundle — no
// fetch, works offline exactly like the server's registry endpoint did. Kept in
// lockstep with automations.mjs's BOT_REGISTRY / bots/medplum.config.json; the
// five undescribed bots (schedule-job-operation, patient-merge-operation,
// care-team-operation, care-plan-operation, no-show-sweeper) fall through to the
// server's exact defaults ('Automation' / 'subscription'), a pre-existing gap.
const BOT_REGISTRY = {
  'review-report-task': { purpose: 'Abnormal lab result → a review-report task for a clinician', trigger: 'subscription' },
  'patient-dedup-task': { purpose: 'Possible duplicate patient → a verify-id task for reception', trigger: 'subscription' },
  'intake-extract': { purpose: 'Normalizes a submitted intake into conditions, allergies, meds and observations', trigger: 'subscription' },
  'respond-to-message-task': { purpose: 'Patient message unanswered past its SLA → a respond task for the front desk', trigger: 'cron' },
  'appointment-reminder': { purpose: "Tomorrow's appointments → a reminder message to each patient", trigger: 'cron' },
  'gupshup-bridge': { purpose: 'Sends WhatsApp/SMS and threads inbound replies into patient conversations', trigger: 'operation' },
  'overdue-task-sweeper': { purpose: 'Marks tasks overdue and escalates them when they sit too long', trigger: 'cron' },
  'finalize-report': { purpose: 'Finalizes a completed diagnostic report and links its results', trigger: 'subscription' },
  'bmi-derived-vital': { purpose: 'Computes BMI as a derived vital when height and weight are recorded', trigger: 'subscription' },
  'scheduled-job-dispatcher': { purpose: 'The heartbeat that fires every recurring reminder/message you schedule', trigger: 'cron' },
};
// The bot ORDER matches bots/medplum.config.json's `bots` array (what loadRegistry
// iterates when the manifest reads). The name→label transform is loadRegistry's:
// hyphens→spaces, Title Case.
const BOT_NAMES = [
  'review-report-task', 'patient-dedup-task', 'intake-extract', 'respond-to-message-task',
  'appointment-reminder', 'gupshup-bridge', 'overdue-task-sweeper', 'no-show-sweeper',
  'finalize-report', 'bmi-derived-vital', 'scheduled-job-dispatcher', 'schedule-job-operation',
  'patient-merge-operation', 'care-team-operation', 'care-plan-operation',
];
function labelFor(name) {
  return name.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
export const AUTOMATIONS_REGISTRY = BOT_NAMES.map((name) => ({
  name,
  label: labelFor(name),
  purpose: (BOT_REGISTRY[name] && BOT_REGISTRY[name].purpose) || 'Automation',
  trigger: (BOT_REGISTRY[name] && BOT_REGISTRY[name].trigger) || 'subscription',
}));

// Test surface — the pure summary/identification helpers, exercised offline
// (mirrors automations.mjs's __test so the same offline assertions port).
export const __test = {
  readJob, writeJobExt, scheduleSummary, cohortSummary, shapeJob,
  looksBotTask, commCategoryCode, explainJob, patientName,
  nextOccurrenceMs, lastOccurrenceMs, AUTOMATIONS_REGISTRY,
  jobBucket, groupJobs, taskSummary, normTask, taskCompatMessage,
  validateTask, validateCohort, TASK_TYPES, TASK_TYPE_LABELS,
};
